// Única fuente del texto de la Biblioteca (D-22): el árbol de escritorio, el móvil, las
// etiquetas accesibles y los titulares de las páginas de funcionalidad leen de aquí, así que
// una descripción no puede decir una cosa en 1440 y otra en 390.
//
// Copiado de funciones() de Biblioteca_Movil.dc.html, que coincide con las etiquetas y con
// titulos() de Biblioteca.dc.html, con dos correcciones de contenido que ganan al tablero:
// - BI-1 (D-22): Plantillas hablaba de una plantilla del terapeuta; no hay plantillas propias,
//   solo las siete incluidas, así que dice «la plantilla».
// - BI-2 (D-23, D-29): Psicometría conserva «interpreta las escalas que tú puntúas» (del
//   dueño), pero cambia «percentiles» por «evolución»: los percentiles salen del documento
//   corregido y la aplicación no aplica baremos.
import type { ClaveBiblioteca } from '@/lib/marketing/geometriaBiblioteca';

// Las figuras del icono en su viewBox de 16, tal cual las dibuja el tablero.
export type FiguraIcono =
    | { readonly tipo: 'trazo'; readonly d: string }
    | { readonly tipo: 'rect'; readonly x: number; readonly y: number; readonly ancho: number; readonly alto: number; readonly radio: number }
    | { readonly tipo: 'circulo'; readonly cx: number; readonly cy: number; readonly r: number };

export interface TextoFuncion {
    readonly nombre: string;
    readonly descripcion: string;
    // Titular de la sección cuando la página de esa funcionalidad la destaca.
    readonly titulo: string;
    readonly figuras: readonly FiguraIcono[];
    // El icono del plan es el único del tablero sin extremos redondeados.
    readonly extremosRectos?: boolean;
}

export const TEXTOS_FUNCIONES: Readonly<Record<ClaveBiblioteca, TextoFuncion>> = {
    sesiones: {
        nombre: 'Sesiones',
        descripcion: 'transcribe y resume cada sesión, con su nota SOAP',
        titulo: 'Dónde encajan las sesiones en la memoria de tu paciente',
        figuras: [
            { tipo: 'rect', x: 5.75, y: 1.75, ancho: 4.5, alto: 8, radio: 2.25 },
            { tipo: 'trazo', d: 'M3.25 7.5a4.75 4.75 0 0 0 9.5 0M8 12.25v2' },
        ],
    },
    plantillas: {
        nombre: 'Plantillas',
        descripcion: 'rellena la plantilla clínica con lo que ya consta en el historial',
        titulo: 'Dónde encajan las plantillas en la memoria de tu paciente',
        figuras: [
            { tipo: 'rect', x: 2.5, y: 2.5, ancho: 11, alto: 11, radio: 1.5 },
            { tipo: 'trazo', d: 'M2.5 6h11M6.5 6v7.5' },
        ],
    },
    informes: {
        nombre: 'Informes',
        descripcion: 'redacta el informe por secciones para que tú lo revises',
        titulo: 'Dónde encajan los informes en la memoria de tu paciente',
        figuras: [{ tipo: 'trazo', d: 'M4 1.5h5l3 3v10H4z M9 1.5v3h3 M6 8h4M6 10.5h4' }],
    },
    psicometria: {
        nombre: 'Psicometría',
        descripcion: 'interpreta las escalas que tú puntúas: perfil, evolución y discrepancias',
        titulo: 'Dónde encaja la psicometría en la memoria de tu paciente',
        figuras: [{ tipo: 'trazo', d: 'M2.5 13.5h11M4.5 13.5V9M8 13.5V4M11.5 13.5V7' }],
    },
    manuscritos: {
        nombre: 'Manuscritos',
        descripcion: 'transcribe tus notas a mano y revisa cuestionarios escaneados',
        titulo: 'Dónde encajan los manuscritos en la memoria de tu paciente',
        figuras: [
            { tipo: 'trazo', d: 'M10.5 2.5l3 3L6 13H3v-3z' },
            { tipo: 'trazo', d: 'M9 4l3 3' },
        ],
    },
    plan: {
        nombre: 'Plan de tratamiento',
        descripcion: 'reúne objetivos y tareas; la IA propone y tú apruebas',
        titulo: 'Dónde encaja el plan de tratamiento en la memoria de tu paciente',
        figuras: [
            { tipo: 'circulo', cx: 8, cy: 8, r: 5.5 },
            { tipo: 'circulo', cx: 8, cy: 8, r: 2.5 },
        ],
        extremosRectos: true,
    },
    notas: {
        nombre: 'Notas fijas',
        descripcion: 'lo que el asistente debe tener siempre en cuenta',
        titulo: 'Dónde encajan las notas fijas en la memoria de tu paciente',
        figuras: [{ tipo: 'trazo', d: 'M6 2h4l-.5 4 2 2H4.5l2-2z M8 8v6' }],
    },
    carpetas: {
        nombre: 'Carpetas',
        descripcion: 'ordena sesiones y documentos por carpeta y por fecha',
        titulo: 'Dónde encajan las carpetas en la memoria de tu paciente',
        figuras: [{ tipo: 'trazo', d: 'M1.5 4.5v8h13v-7H8L6.5 3.5h-5z' }],
    },
};

export const TEXTOS_SECCION = {
    tituloReposo: 'Ocho funciones, una sola memoria por paciente',
    entradillaReposoEscritorio:
        'Cada cosa que registras se guarda en su sitio y queda unida al resto. Elige una función, o su zona de la biblioteca, para ver qué hace.',
    entradillaReposoMovil: 'Cada cosa que registras se guarda en su sitio y queda unida al resto. Toca una función para ver qué hace.',
    entradillaDestacadoEscritorio: 'Es una de las ocho partes de la memoria de cada paciente. Elige cualquier otra para ver qué hace.',
    entradillaDestacadoMovil: 'Es una de las ocho partes de la memoria de cada paciente. Toca cualquier otra para ver qué hace.',
    tituloTarjeta: 'La memoria de Marta Ruiz',
    sesiones: 12,
    panelReposo: 'Toca una función para ver qué hace.',
    paciente: 'Marta Ruiz, paciente de ejemplo',
    inicialesPaciente: 'MR',
} as const;

export function etiquetaFuncion(texto: TextoFuncion): string {
    return `${texto.nombre}: ${texto.descripcion}`;
}

export function contadorRegistros(registros: number): string {
    return `${TEXTOS_SECCION.sesiones} sesiones · ${registros} registros`;
}
